import { ajv } from '@cibo/core'
import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { useForm } from 'uniforms'
import { AutoForm } from 'uniforms-mui'

// Normal validator helper
function createValidator(schema: any) {
  const validate = ajv.compile(schema)
  return (model: any) => {
    const errors: Record<string, string> = {}
    validate(model)
    if (validate.errors) {
      for (const err of validate.errors) {
        const path = err.instancePath.slice(1) // remove leading "/"
        errors[path] = err.message || 'Error'
      }
    }
    return errors
  }
}

const CiboAutoFormContext = createContext<{
  warnings?: Record<string, string>
  touch: (name: string) => void
  touched: Record<string, boolean>
  displays?: string[]
}>({ touched: {}, touch: () => {} })

export function CiboAutoForm({ model, schema, warningSchema, ...props }: any) {
  const [warnings, setWarnings] = useState<Record<string, string>>({})

  const warningValidator = useMemo(() => {
    return warningSchema && createValidator(warningSchema)
  }, [warningSchema])

  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const touch = useCallback((name: string) => {
    setTouched(prev => ({ ...prev, [name]: true }))
  }, [])

  return (
    <CiboAutoFormContext.Provider value={{ warnings, touch, touched }}>
      <AutoForm
        schema={schema}
        model={model}
        {...props}
        onChangeModel={newModel => {
          const warningResults = warningValidator?.(newModel)
          setWarnings(warningResults)

          props.onChangeModel?.(newModel)
        }}
      >
        {props.children}
      </AutoForm>
    </CiboAutoFormContext.Provider>
  )
}

export const useCiboAutoForm = () => {
  const form = useForm()
  const context = useContext(CiboAutoFormContext)

  return { ...form, ...context }
}
